/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React from 'react';
import { LayoutBase } from '../base/LayoutBase';
import { ExploreCardView } from './ExploreCardView';
import CompareUtils from './compare/CompareUtils';
import { CompareCardView } from './compare/CompareCardView';
import { CometCardView } from './comet/CometCardView';
import { openCardsPane } from 'util/SidePanelUtil';

const ExploreCard = class ExploreCard extends LayoutBase {
	constructor(props){
		super(props);

		if(this.model.isCompareEnabled) {
			//@todo we should not access internal (dashboardApi) from layout controller directly
			this._compareUtils = new CompareUtils({ services: this.services, model: this.model, dashboardApi: this.layoutController.dashboardApi });
			this.Component = CompareCardView;
		} else {
			this.Component = ExploreCardView;
		}


		this._tempParentNode = props.tempParentNode || this._tempParent || document.createElement('div');
		this._tempParentNode.style.visibility = 'hidden';
		const main = document.getElementById('main');
		main.appendChild(this._tempParentNode);

		this._widgetPromises = {};


		//CC
		this._widgetNodeMap = {};
	}

	getWidgetPromises(widgetId) {
		if(!this._widgetPromises[widgetId]) {
			const promisesObject = {};
			const domNodePromise = new Promise((resolve) => {
				promisesObject.setDomNode = function(val) {
					resolve(val);
				};
			});
			const markupPromise = new Promise((resolve) => {
				promisesObject.setMarkup = function(val) {
					resolve(val);
				};
			});
			promisesObject.allPromisesForOneWidget = [domNodePromise, markupPromise];

			Promise.all([domNodePromise, markupPromise]).then((data) => {
				const domNode = data[0], markup = data[1];
				domNode.appendChild(markup);
				this._widgetNodeMap[widgetId] = markup;
				this._resolvedChild && this._resolvedChild(widgetId); //for debugging
			});
			this._widgetPromises[widgetId] = promisesObject;
		}
		return this._widgetPromises[widgetId];
	}

	getNodeId(id) {
		return id;
	}

	trackChildWidget(widgetLayoutView) {
		if( this.addChildRenderMarkup( widgetLayoutView.id, widgetLayoutView )){
			this.setupShowBy(widgetLayoutView.id);
			if (this._compareUtils) {
				this._compareUtils.initialize(widgetLayoutView.id, this.model);
			}
			this.model.incrChildReadyCount();
		}
		this.getWidgetPromises(widgetLayoutView.id).setMarkup(widgetLayoutView.domNode);
	}

	setupShowBy(id) {
		//widget has the same id
		return this.dashboardApi.getCanvasWhenReady()
			.then(canvas => {
				const state = canvas.getContent(id).getFeature('state');
				return state.whenStatusChanges(state.STATUS.RENDERED);
			})
			.then(() => {
				const content = this.dashboardApi.getCanvas().getContent(id);
				const showByAction = content && content.getFeature('ShowByAction');
				showByAction && showByAction.setOnSelectCallback({ cb: this.addShowByCard.bind(this) });
			});
	}

	/**
	 * Add a show by/compare by card
	 * @param {Array} result - an array of widget spec(s) - one spec for show by; two for compare by
	 * @param {Object} payloadData - object containing undoRedoTransactionId passed from showByFeature
	 */
	addShowByCard(result, payloadData) {
		if( result && result.length > 0 && result.length <= 2 ) {
			const models = [
				{ model:result[0] }
			];
			let params;
			if(result.length === 2) {
				models.push({
					model:result[1]
				});
				params = {
					model: {
						subType: 'compare'
					},
					selectAfterAdd: true
				};
			} else {
				params = {
					selectAfterAdd: true
				};
			}
			const layoutModel = this.parentLayout.model;
			const newLayoutId = layoutModel.addVisCard(models, payloadData, params/*new card options*/);
			if( result.length === 2 ){
				layoutModel.cardModelUtil.copyPageContextAndRescope(layoutModel, {
					layoutId: this.id,
					cloneId: newLayoutId
				});
			}
		}
	}

	_beforeOnShow(widgetViewId, widgetView) {
		const childViewNode = document.getElementById( widgetViewId );
		if( childViewNode ){
			this._tempParentNode.appendChild(childViewNode);
			this.addChildRenderMarkup( widgetViewId, widgetView );
			this.model.incrChildReadyCount();
		}
	}

	_onAddCometVisualization(recommendation) {
		this._onAddVisualization(recommendation, 'addRelatedRecommendation');
	}

	_onClickVisualizationFromFooterView(recommendation){
		this._onAddVisualization(recommendation, 'exploreSelectCometRecommendation');
	}

	_onAddVisualization(recommendation, operation) {
		const widgetSpec = this.model.cometViewModel.covertRecommendationToWidgetSpec(recommendation);
		this.parentLayout.view.addVisCard(widgetSpec, operation);
		openCardsPane(this.layoutController.dashboardApi);
	}

	triggerWidgetRender(){
		const exploreVM = this.parentLayout.model.exploreVM;
		if (exploreVM) {
			const selected = exploreVM.isCardDisplayed(this.id);
			if( selected ) {
				const contentNode = document.querySelector(`[data-id=${this.model.id}] .widgetContent[rendercount]`);
				const widgetRenderedChildren =  contentNode ?  contentNode.childElementCount : 0;
				if( widgetRenderedChildren === 0 ) {
					this._callChildrenFunction('onShow');
				}
			}
		}
	}

	/**
	 * Normally, triggerWidgetRender is sufficient to ensure a card and its widgets are rendered, however;
	 * when moving to a new card (tab) which has previously been rendered as a thumbnail, we need to ensure the render
	 * occurs to add interactive features to the rendering.
	 */
	triggerOnShowCard(){

		const trigger = ()=>{
			let resizeDone;
			if( this.resizePendingOnShow ) {
				this.resizePendingOnShow = false;
				this.resize();
				resizeDone = 1;
			} else {
				this._callChildrenFunction('onShow');
				resizeDone = 0;
			}
			return resizeDone;
		};

		const selected = this.parentLayout.model.exploreVM.isCardDisplayed(this.id);
		if( selected ) {
			return this._waitTillChildLayoutsReady().then( ()=>{
				return trigger();
			} );
		}
		return Promise.resolve();
	}


	_waitTillChildLayoutsReady() {
		const allPromises = [];
		const entries = Object.values(this._widgetPromises);
		entries.forEach( (entry) => {
			entry.allPromisesForOneWidget.forEach( (prom) => {
				allPromises.push(prom);
			});
		});
		return Promise.all( allPromises );
	}

	showVisTypesDialog(destinationId) {
		this.model.setShowModalVisTypePicker(true, destinationId);
	}

	closeVisTypeChooser() {
		this.model.setShowModalVisTypePicker(false);
	}

	replaceWithWidget(widgetSpec, layoutToBeReplaced) {
		return this.model.replaceWidgetInCard(widgetSpec, layoutToBeReplaced, 'cardCreate');
	}

	placeWidgetInCard(widgetSpec, payload) {
		return this.model.placeWidgetInCard(widgetSpec, payload);
	}

	createVisFromVisType(visTypeId, replaceLayoutId) {
		const widgetSpec = {
			model: {
				type: 'live',
				visId: visTypeId,
				visTypeLocked: true,
				data: {}
			}
		};
		this.replaceWithWidget(widgetSpec, replaceLayoutId);
	}

	removeChild(layoutView) {
		layoutView && this._removeChild(layoutView.id);
	}

	_removeChild(layoutViewId) {
		this.removeChildRenderMarkup(layoutViewId);
		if (this._widgetPromises[layoutViewId]){
			delete this._widgetPromises[layoutViewId];
		}
		if( this._widgetNodeMap[layoutViewId] ){
			delete this._widgetNodeMap[layoutViewId];
		}
	}

	_onModelRemoveItem(e) {
		this.destroy(e);
	}
	_destroyWidgets(e) {
		const childLayoutMap = this._widgetNodeMap || {};
		Object.entries(childLayoutMap).forEach( ( entry) => {
			this._removeChild(entry[1].id);

			if( entry[1]._layout) {
				entry[1]._layout.destroy(e);
				entry[1]._layout = null;
			}
		});
	}

	destroy(e) {
		if (this.layoutController && e && e.value && e.value.parameter === this.model.id ) {
			if (this._tempParentNode) {
				if (this._tempParentNode.remove) {
					// non-IE
					this._tempParentNode.remove();
				} else if (this._tempParentNode.removeChild) {
					// IE
					const children = this._tempParentNode.children;
					if (children.length > 0) {
						for (const child of children) {
							this._tempParentNode.removeChild(child);
						}
					}
				}
			}
			this._tempParentNode = null;
			this._destroyWidgets(e);
			if( this.view ) {
				this.view._alive = false;
			}
			super.destroy(e);
		} else {
			this.logger.warn('destroy not done for layout ' + this.model.type + ' id ' + this.id );
		}
	}

	getRenderMarkup() {
		if (this.model.isCompareEnabled) { // compare card
			return ( <CompareCardView model={this.model}>
				{(getHeaderView, getWidgetHeaderMap, getFooterView, getCssClasses, getCrossHairMatchingPoints, renderPopOver) => {
					return <ExploreCardView
						getWidgetHeaderMap={getWidgetHeaderMap}
						footerView={getFooterView()}
						layoutOwner={this}
						headerView={getHeaderView}
						model={this.model}
						layoutClasses={getCssClasses}
						cardLayoutVM={this.cardLayoutVM}
						layoutController={this.layoutController}
						renderPopOver={renderPopOver}
						getCrossHairMatchingPoints={getCrossHairMatchingPoints}
						services={this.services}>
					</ExploreCardView>;
				}}
			</CompareCardView> );
		} else { // comet or explore card
			return <CometCardView
				onAddItem={this._onAddCometVisualization.bind(this)}
				onClickItem={this._onClickVisualizationFromFooterView.bind(this)}
				model={this.model}
				dashboardApi={this.layoutController.dashboardApi}
				dataSourceId={this.layoutController.boardModel.layout.exploreVM.activeDataSourceId}
				layoutOwner={this}
				logger={this.logger}>
				{(getHeaderView, getWidgetHeaderMap, getFooterView, getCssClasses) => {
					return <ExploreCardView
						layoutOwner={this}
						model={this.model}
						layoutClasses={getCssClasses}
						cardLayoutVM={this.cardLayoutVM}
						layoutController={this.layoutController}
						footerView={getFooterView()}
						services={this.services}>
					</ExploreCardView>;
				}}
			</CometCardView>;
		}
	}

	onItemAddDone() {
		this.model.cometViewModel.hookupCustomDataHandler();
	}

	resizeIfVisible() {
		if(this.model.isDisplayed) {
			this.resize();
		} else {
			this.resizePendingOnShow = true;
		}
	}
};



export default ExploreCard;



// WEBPACK FOOTER //
// ./src/js/layout/explorecard/ExploreCard.js